import { useMemo } from 'react';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import { PermissionAction, PermissionResource, useGetUser } from '@sib/api';
import { useAutoLogout, useCan, useUserSetupStore } from '@sib/auth';
import {
  DOCUMENTS_DROPZONE_SUPPORTED_FORMATS,
  DOCUMENTS_MAX_SIZE_GB,
} from '@sib/config';
import { useTranslation } from '@sib/i18n';
import { tracking, usePageTracking } from '@sib/tracking';
import { FileUploadMode } from '@sib/types';
import { cn, Sidebar, Layout, FileDropDialog } from '@sib/ui';
import { DOCUMENTS_DROPZONE_CONFIG, useFileDropDialog } from '@sib/utils';
import {
  ActivateAiModal,
  AppStoreBadges,
  AskAiModal,
  FileUploadContext,
  SpendbrainDialog,
  TermsOfServiceModal,
  UploadQueueGuardDialog,
  UserWelcomeCard,
} from '@spendbrain/common';
import { useAskAi, useUploadQueueGuardDialog } from '@spendbrain/common/hooks';
import { LayoutProps } from '@spendbrain/common/types/layout';
import { updateUserId } from '@spendbrain/common/utils';
import { BadgeLocation, MEDIA_SM_QUERY } from '@spendbrain/config/constants';
import { paths } from '@spendbrain/config/paths';
import {
  useMediaQuery,
  useExternalToken,
  useAppStoreBadges,
} from '@spendbrain/hooks';
import { useFileManagerFileUploadStore } from '@spendbrain/stores/fileUploadStore';
import { useSpendbrainVersionCheck } from '@spendbrain/utils/version';
import { useFeedbackStore, useSidebarStore } from '../../stores';
import { FeedbackDialog } from '../FeedbackDialog';
import { LogoutDialog } from '../LogoutDialog';
import { NewVersionDialog } from '../NewVersionDialog';
import { PhoneNumberModal } from '../PhoneNumberModal';
import { TopBar } from '../TopBar';
import { AskAiButton } from './AskAiButton';
import {
  useOrganizationSetup,
  useBeforeUnloadWarning,
  usePhoneNumberModal,
  useTermsOfServiceModal,
  useSidebarItems,
} from './DefaultLayout.utils';

export const DefaultLayout = ({ children }: LayoutProps) => {
  const { t } = useTranslation();

  useOrganizationSetup();
  useExternalToken();
  usePageTracking();
  useBeforeUnloadWarning();
  useAutoLogout({ onLogout: updateUserId });

  const { data: identity } = useGetUser();
  const {
    isOpen: isSidebarOpen,
    toggleSidebar,
    setSidebarOpen,
  } = useSidebarStore();
  const { closeFeedbackDialog, isFeedbackDialogOpen } = useFeedbackStore();
  const isMobile = useMediaQuery(MEDIA_SM_QUERY);
  const { files } = useFileManagerFileUploadStore();
  const { isNewVersionAvailable, onAppReload } = useSpendbrainVersionCheck();
  const { data: user, refetch: refetchUser } = useGetUser();
  const { items: sidebarItems, logoutDialog, logout } = useSidebarItems();
  const fileUploadStore = useFileManagerFileUploadStore();

  const {
    getInputProps,
    getRootProps,
    closeDialog,
    currentMode,
    onModeChange,
    openDialog,
  } = useFileDropDialog({
    fileUploadStore,
    dropzoneConfig: DOCUMENTS_DROPZONE_CONFIG,
  });
  const can = useCan();
  const {
    isOpen: isPhoneNumberModalOpen,
    handleSuccess: handlePhoneNumberSuccess,
  } = usePhoneNumberModal({ user, refetchUser });
  const {
    organization: { id: organizationId },
  } = useUserSetupStore();
  const {
    isOpen: isTermsOfServiceModalOpen,
    handleAccept: handleTOSAccept,
    ...termsOfServiceModal
  } = useTermsOfServiceModal({ organizationId });
  const { isDialogOpen: isUploadQueueGuardDialogOpen, dialogProps } =
    useUploadQueueGuardDialog();

  const openFileDropDialog = () => {
    openDialog(
      files.length ? FileUploadMode.FileManager : FileUploadMode.Dropzone
    );
    tracking.track('Opened upload file dialog');
    tracking.startSessionRecording();
  };

  const openFileManager = () => {
    dialogProps.onClose();
    openDialog(FileUploadMode.FileManager);
  };

  const appStoreBadgesProps = useAppStoreBadges({
    location: BadgeLocation.SIDE_PANEL,
  });

  const {
    isAiModalOpen,
    isAskAiModalOpen,
    organization,
    customBrandName,
    handleCloseAiModal,
    handleCloseAskAiModal,
    handleAskAi,
  } = useAskAi();
  const buttonLabel = customBrandName || t('askSpendBrain');

  const path = useLocation();
  const navigate = useNavigate();
  const isOnDocumentsRepostioryPage = useMemo(
    () => Boolean(matchPath(paths.documents.root, path.pathname)),
    [path.pathname]
  );

  return (
    <>
      <LogoutDialog {...logoutDialog} onLogout={logout} />
      <FileUploadContext.Provider value={{ openDialog: openFileDropDialog }}>
        <Layout
          {...getRootProps()}
          topbar={
            <TopBar
              onNavOpen={toggleSidebar}
              onUploadClick={openFileDropDialog}
            />
          }
          sidebar={
            <Sidebar
              onChange={setSidebarOpen}
              items={sidebarItems}
              open={isSidebarOpen}
              fullWidth={isMobile}
              hideArrowButton
              applicationName={__APP_DISPLAY_NAME__}
              applicationVersion={__APP_VERSION__}
              header={
                <div className="pb-4">
                  <div
                    className={cn(
                      'w-full max-w-0 overflow-hidden pb-4 transition-all duration-300',
                      isSidebarOpen && 'max-w-64'
                    )}
                  >
                    <div className="flex w-64 items-center justify-center">
                      <UserWelcomeCard
                        email={identity?.email || ''}
                        name={identity?.name || ''}
                        avatarUrl={identity?.avatarUrl || ''}
                      />
                    </div>
                  </div>
                  <div
                    className={cn(
                      'flex w-64 justify-center transition-all duration-300',
                      !isSidebarOpen && 'w-13'
                    )}
                  >
                    <AskAiButton
                      shrink={!isSidebarOpen}
                      buttonLabel={buttonLabel}
                      handleAskAi={handleAskAi}
                    />
                  </div>
                </div>
              }
              footer={
                <div
                  className={cn(
                    'flex w-full max-w-0 flex-[1_1] flex-col items-center justify-end overflow-hidden pb-4 transition-all duration-300',
                    isSidebarOpen && 'max-w-64'
                  )}
                >
                  <div className="w-64">
                    <AppStoreBadges
                      {...appStoreBadgesProps}
                      layout="vertical"
                      className="justify-end"
                    />
                  </div>
                </div>
              }
            />
          }
        >
          <input {...getInputProps()} />
          <FeedbackDialog
            open={isFeedbackDialogOpen}
            onClose={closeFeedbackDialog}
          />
          <UploadQueueGuardDialog
            {...dialogProps}
            open={isUploadQueueGuardDialogOpen}
            onOpenFileManager={openFileManager}
          />
          <SpendbrainDialog />
          <NewVersionDialog
            open={isNewVersionAvailable}
            onReload={onAppReload}
          />
          <ActivateAiModal
            isOpen={isAiModalOpen}
            onClose={handleCloseAiModal}
            organization={organization}
          />
          <AskAiModal
            isOpen={isAskAiModalOpen}
            onClose={handleCloseAskAiModal}
            title={buttonLabel}
          />
          {user?.id && (
            <PhoneNumberModal
              open={isPhoneNumberModalOpen}
              userId={user.id}
              onSuccess={handlePhoneNumberSuccess}
            />
          )}
          <TermsOfServiceModal
            open={isTermsOfServiceModalOpen}
            onAccept={handleTOSAccept}
            onClose={termsOfServiceModal.onClose}
            acceptedByUser={termsOfServiceModal.acceptedByUser}
            canAccept={termsOfServiceModal.canAccept}
            isDismissible={termsOfServiceModal.isDismissible}
          />
          {can({
            permissions: {
              actions: PermissionAction.CREATE,
              resources: PermissionResource.Documents,
            },
          }) && (
            <FileDropDialog
              config={{
                entityName: t('entities.documents'),
                supportedFormats: DOCUMENTS_DROPZONE_SUPPORTED_FORMATS,
                maxFileSize: `${DOCUMENTS_MAX_SIZE_GB} GB`,
                ...(!isOnDocumentsRepostioryPage && {
                  onSuccessNotificationAction: {
                    label: t(
                      'fileManager.uploadSuccess.redirectToDocumentsRepository'
                    ),
                    onClick: () => navigate(paths.documents.root),
                    ActionButtonProps: {
                      variant: 'ghost',
                    },
                  },
                }),
              }}
              fileUploadStore={fileUploadStore}
              onClose={closeDialog}
              onModeChange={onModeChange}
              currentMode={currentMode}
            />
          )}

          {children}
        </Layout>
      </FileUploadContext.Provider>
    </>
  );
};

DefaultLayout.displayName = 'DefaultLayout';
